import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  CircularProgress,
  InputAdornment,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import type { CatalogoItemDto } from '../../services/vehiculoService';

export interface CatalogoCrudDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (item: CatalogoItemDto) => void;
  title: string;
  codigoLabel?: string;
  nombreLabel?: string;
  /** Límite de caracteres del código (según campo en BD). */
  codigoMaxLength?: number;
  list: (searchTerm: string) => Promise<CatalogoItemDto[]>;
  create: (codigo: string, descripcion: string) => Promise<CatalogoItemDto>;
  update: (codigo: string, descripcion: string) => Promise<void>;
  remove: (codigo: string) => Promise<void>;
}

const CatalogoCrudDialog: React.FC<CatalogoCrudDialogProps> = ({
  open,
  onClose,
  onSelect,
  title,
  codigoLabel = 'Código',
  nombreLabel = 'Nombre',
  codigoMaxLength,
  list,
  create,
  update,
  remove,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const formRef = useRef<HTMLFormElement>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [items, setItems] = useState<CatalogoItemDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<CatalogoItemDto | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [isEditForm, setIsEditForm] = useState(false);
  const [formCodigo, setFormCodigo] = useState('');
  const [formNombre, setFormNombre] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(
    async (term: string) => {
      try {
        setLoading(true);
        setError(null);
        const data = await list(term);
        setItems(data);
        setSelected((prev) => {
          if (!prev) return null;
          return data.find((i) => i.codigo === prev.codigo) ?? null;
        });
      } catch (err: unknown) {
        console.error('Error al cargar catálogo:', err);
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Error al cargar el catálogo';
        setError(msg);
        setItems([]);
        setSelected(null);
      } finally {
        setLoading(false);
      }
    },
    [list],
  );

  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setItems([]);
      setError(null);
      setSelected(null);
      setFormOpen(false);
      return;
    }
    const timeoutId = setTimeout(() => {
      fetchItems(searchTerm);
    }, searchTerm ? 300 : 0);
    return () => clearTimeout(timeoutId);
  }, [open, searchTerm, fetchItems]);

  const openNuevo = () => {
    setIsEditForm(false);
    setFormCodigo('');
    setFormNombre('');
    setFormOpen(true);
  };

  const openEditar = () => {
    if (!selected) {
      enqueueSnackbar('Seleccione un ítem para editar', { variant: 'warning' });
      return;
    }
    setIsEditForm(true);
    setFormCodigo(selected.codigo);
    setFormNombre(selected.descripcion);
    setFormOpen(true);
  };

  const handleDelete = async () => {
    if (!selected) {
      enqueueSnackbar('Seleccione un ítem para eliminar', { variant: 'warning' });
      return;
    }
    if (!window.confirm(`¿Eliminar "${selected.codigo} - ${selected.descripcion}"?`)) return;
    try {
      await remove(selected.codigo);
      enqueueSnackbar('Eliminado exitosamente', { variant: 'success' });
      setSelected(null);
      fetchItems(searchTerm);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Error al eliminar';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    const codigo = formCodigo.trim();
    const descripcion = formNombre.trim();
    if (!codigo) {
      enqueueSnackbar(`${codigoLabel} es requerido`, { variant: 'warning' });
      return;
    }
    if (!descripcion) {
      enqueueSnackbar(`${nombreLabel} es requerido`, { variant: 'warning' });
      return;
    }
    try {
      setSaving(true);
      if (isEditForm) {
        await update(codigo, descripcion);
        enqueueSnackbar('Guardado exitosamente', { variant: 'success' });
        setSelected({ codigo, descripcion });
      } else {
        const created = await create(codigo, descripcion);
        enqueueSnackbar('Creado exitosamente', { variant: 'success' });
        setSelected(created);
      }
      setFormOpen(false);
      fetchItems(searchTerm);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Error al guardar';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!formOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F2') {
        e.preventDefault();
        e.stopPropagation();
        if (!saving) formRef.current?.requestSubmit();
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        if (!saving) setFormOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [formOpen, saving]);

  const handleSeleccionar = () => {
    if (!selected) {
      enqueueSnackbar('Seleccione un ítem de la lista', { variant: 'warning' });
      return;
    }
    onSelect(selected);
    onClose();
  };

  const handleRowDoubleClick = (item: CatalogoItemDto) => {
    onSelect(item);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {title}
          <IconButton size="small" onClick={onClose} aria-label="Cerrar">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openNuevo} size="small">
              Nuevo
            </Button>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={openEditar}
              disabled={!selected}
              size="small"
            >
              Editar
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              disabled={!selected}
              size="small"
            >
              Eliminar
            </Button>
          </Stack>

          <TextField
            fullWidth
            placeholder="Buscar por código o descripción..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" />
                </InputAdornment>
              ),
            }}
          />

          {loading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={32} />
            </Box>
          ) : error ? (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          ) : items.length === 0 ? (
            <Typography color="text.secondary" variant="body2">
              No se encontraron resultados.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>{codigoLabel}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => {
                    const isSelected = selected?.codigo === item.codigo;
                    return (
                      <TableRow
                        key={item.codigo}
                        hover
                        selected={isSelected}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setSelected(item)}
                        onDoubleClick={() => handleRowDoubleClick(item)}
                      >
                        <TableCell>{item.codigo}</TableCell>
                        <TableCell>{item.descripcion}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose}>Salir</Button>
          <Tooltip title="Doble clic también selecciona">
            <span>
              <Button variant="contained" onClick={handleSeleccionar} disabled={!selected}>
                Seleccionar
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      <Dialog open={formOpen} onClose={() => !saving && setFormOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {isEditForm ? `Editar ${title}` : `Nuevo — ${title}`}
        </DialogTitle>
        <form ref={formRef} onSubmit={handleFormSubmit}>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <TextField
                label={codigoLabel}
                value={formCodigo}
                onChange={(e) =>
                  setFormCodigo(
                    codigoMaxLength != null
                      ? e.target.value.slice(0, codigoMaxLength)
                      : e.target.value,
                  )
                }
                fullWidth
                required
                disabled={isEditForm || saving}
                autoFocus={!isEditForm}
                inputProps={codigoMaxLength != null ? { maxLength: codigoMaxLength } : undefined}
              />
              <TextField
                label={nombreLabel}
                value={formNombre}
                onChange={(e) => setFormNombre(e.target.value)}
                fullWidth
                required
                disabled={saving}
                autoFocus={isEditForm}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFormOpen(false)} disabled={saving}>
              ESC-Cancelar
            </Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? <CircularProgress size={22} /> : 'F2-Aceptar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default CatalogoCrudDialog;
